import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseApp";
import { httpClient } from "../api/httpClient";
import { Endpoints } from "../constants/endpoint.constant";
import { formatDateTimeZA } from "../utils/date.util";
import {
  SPEED_INFRINGEMENT_SETTINGS_COLLECTION,
  SPEED_INFRINGEMENT_SETTINGS_DOC,
  normalizeSpeedInfringementRules,
} from "../constants/speedInfringementRules.constant";

const CONNECTION_LIVE_MS = 5 * 1000;
const CONNECTION_OFFLINE_MS = 15 * 1000;
const TERMINAL_TRIP_STATUSES = new Set([4, 5]);

export function toMillis(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?._seconds === "number") return value._seconds * 1000;
  return null;
}

export function formatDateTime(value) {
  const millis = toMillis(value);
  if (!millis) return "Not available";
  return formatDateTimeZA(millis);
}

export function formatSpeed(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1)} km/h` : "Not available";
}

export function formatDurationSeconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return "Not available";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

export function getInfringementActionLabel(actionType) {
  switch ((actionType || "").toString()) {
    case "called_driver_alert":
      return "Called driver due to infringement alert";
    case "called_no_response":
      return "Called and no response";
    case "warning_issued":
      return "Warning issued";
    case "escalated_to_management":
      return "Escalated to management";
    default:
      return actionType || "Not available";
  }
}

function normalizeCoordinate(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function buildCoordinateLabel(position) {
  if (!position) return "Not available";
  return `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
}

function normalizeRoadSpeedLimitKph(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

export function getReviewStatusLabel(status) {
  switch ((status || "pending").toString()) {
    case "accepted":
      return "Accepted";
    case "dismissed":
      return "Dismissed";
    case "uncertain":
      return "Uncertain";
    default:
      return "Pending";
  }
}

export function deriveConnectionStatus({ heartbeatAgeMs, trackingStatusKey }) {
  if (Number.isFinite(heartbeatAgeMs)) {
    if (heartbeatAgeMs <= CONNECTION_LIVE_MS) return "live";
    if (heartbeatAgeMs < CONNECTION_OFFLINE_MS) return "stale";
    return "offline";
  }
  if (trackingStatusKey === "tracking_ended") return "ended";
  if (trackingStatusKey === "tracking_lost" || trackingStatusKey === "prolonged_outage") {
    return "offline";
  }
  return "offline";
}

function providerPausedByInactivity(summary = {}, telemetry = {}) {
  const providerStatus =
    summary?.providerStatus ||
    summary?.speedingAnalysis?.providerStatus ||
    telemetry?.providerStatus ||
    telemetry?.speedingProviderStatus;
  const telemetryStatus = (telemetry?.status || "").toString().toLowerCase();
  const trackingLabel = (summary?.trackingStatusLabel || "").toString().toLowerCase();

  return (
    providerStatus === "paused_inactivity_timeout" ||
    telemetryStatus === "timed_out" ||
    telemetryStatus === "paused" ||
    trackingLabel.includes("timed out") ||
    trackingLabel.includes("paused")
  );
}

function trackingPausedLabel(summary = {}, telemetry = {}) {
  if (providerPausedByInactivity(summary, telemetry)) {
    return "Tracking paused after inactivity timeout";
  }
  return null;
}

function getLatestTrackingSample(samples = []) {
  return [...(samples || [])]
    .filter((sample) => Number.isFinite(Number(sample?.sortAtMs || sample?.recordedAtMs || sample?.createdAtMs)))
    .sort(
      (a, b) =>
        Number(a?.sortAtMs || a?.recordedAtMs || a?.createdAtMs || 0) -
        Number(b?.sortAtMs || b?.recordedAtMs || b?.createdAtMs || 0)
    )
    .at(-1) || null;
}

function getTerminalTrackingState(status) {
  const numericStatus = Number(status);
  if (!TERMINAL_TRIP_STATUSES.has(numericStatus)) return null;
  return {
    isTerminal: true,
    connectionStatus: "ended",
    trackingStatusKey: "tracking_ended",
    trackingStatusLabel: numericStatus === 4 ? "Tracking ended - trip cancelled" : "Tracking ended - trip completed",
  };
}

function normalizeLiveTrackingStatus({ connectionStatus, trackingStatusKey, trackingStatusLabel, telemetry = {} }) {
  const telemetryStatus = (telemetry?.status || "").toString().toLowerCase();
  const hasActiveTelemetry = telemetryStatus === "active" || telemetryStatus === "ready" || telemetryStatus === "connected";
  const staleEndedSummary = trackingStatusKey === "tracking_ended" && connectionStatus !== "ended";

  if (staleEndedSummary || (connectionStatus === "live" && hasActiveTelemetry)) {
    return {
      trackingStatusKey: "tracking_active",
      trackingStatusLabel: "Tracking Active",
    };
  }

  return {
    trackingStatusKey,
    trackingStatusLabel,
  };
}

export function mergeTrackingWithLiveSamples(tracking, trackingSamples = [], now = Date.now()) {
  if (!tracking) return tracking;
  const terminalState = getTerminalTrackingState(tracking?.raw?.status ?? tracking?.status);

  const latestSample = getLatestTrackingSample(trackingSamples);
  const latestSampleAtMs = Number(latestSample?.sortAtMs || latestSample?.recordedAtMs || latestSample?.createdAtMs);
  const currentTelemetryAtMs = Number(tracking?.lastTelemetryAtMs);
  const sampleIsFreshest =
    Number.isFinite(latestSampleAtMs) &&
    (!Number.isFinite(currentTelemetryAtMs) || latestSampleAtMs >= currentTelemetryAtMs);
  const lastTelemetryAtMs = sampleIsFreshest ? latestSampleAtMs : currentTelemetryAtMs;
  const heartbeatAgeMs = terminalState
    ? null
    : Number.isFinite(lastTelemetryAtMs) ? Math.max(0, now - lastTelemetryAtMs) : null;
  const connectionStatus = deriveConnectionStatus({
    heartbeatAgeMs,
    trackingStatusKey: terminalState?.trackingStatusKey || (sampleIsFreshest ? null : tracking?.trackingStatusKey),
  });
  const normalizedStatus = normalizeLiveTrackingStatus({
    connectionStatus: terminalState?.connectionStatus || connectionStatus,
    trackingStatusKey: terminalState?.trackingStatusKey || tracking.trackingStatusKey,
    trackingStatusLabel: terminalState?.trackingStatusLabel || tracking.trackingStatusLabel,
    telemetry: tracking?.telemetry,
  });
  const samplePosition = sampleIsFreshest
    ? normalizeCoordinate(latestSample?.latitude, latestSample?.longitude)
    : null;
  const sampleSpeedKph =
    sampleIsFreshest && Number.isFinite(Number(latestSample?.speedKph))
      ? Number(latestSample.speedKph)
      : null;

  return {
    ...tracking,
    position: samplePosition || tracking.position,
    coordinatesLabel: samplePosition ? buildCoordinateLabel(samplePosition) : tracking.coordinatesLabel,
    speedKph: sampleSpeedKph ?? tracking.speedKph,
    lastTelemetryAtMs,
    lastTelemetryAt: Number.isFinite(lastTelemetryAtMs) ? new Date(lastTelemetryAtMs).toISOString() : null,
    heartbeatAgeMs,
    connectionStatus: terminalState?.connectionStatus || connectionStatus,
    trackingStatusKey: normalizedStatus.trackingStatusKey,
    trackingStatusLabel: normalizedStatus.trackingStatusLabel,
    isTrackingTerminal: Boolean(terminalState),
    telemetryActivitySource: sampleIsFreshest ? "tracking_sample" : tracking.telemetryActivitySource,
  };
}

export function mapTripTrackingSnapshot(snapshot) {
  if (!snapshot?.exists()) return null;

  const data = snapshot.data() || {};
  const telemetry = data?.driverTrackingTelemetry || {};
  const summary = data?.trackingComplianceSummary || {};
  const position =
    normalizeCoordinate(summary?.latestKnownLatitude, summary?.latestKnownLongitude) ||
    normalizeCoordinate(telemetry?.lastKnownRawLatitude, telemetry?.lastKnownRawLongitude) ||
    normalizeCoordinate(telemetry?.lastKnownLatitude, telemetry?.lastKnownLongitude) ||
    normalizeCoordinate(data?.currentLatitude, data?.currentLongitude);
  const lastTelemetryAtMs =
    toMillis(telemetry?.lastHeartbeatAt) ||
    toMillis(telemetry?.finalRecordedAt) ||
    toMillis(summary?.lastHeartbeatAt) ||
    toMillis(summary?.updatedAt) ||
    toMillis(data?.trackingUpdatedAt);
  const terminalState = getTerminalTrackingState(data?.status);
  const pausedLabel = trackingPausedLabel(summary, telemetry);
  const fallbackHeartbeatAgeMs = lastTelemetryAtMs && !terminalState ? Math.max(0, Date.now() - lastTelemetryAtMs) : null;
  const heartbeatAgeMs = terminalState
    ? null
    : Number.isFinite(Number(summary?.heartbeatAgeMs))
    ? Number(summary.heartbeatAgeMs)
    : fallbackHeartbeatAgeMs;
  const connectionStatus = deriveConnectionStatus({
    heartbeatAgeMs,
    trackingStatusKey: pausedLabel ? "tracking_paused" : terminalState?.trackingStatusKey || summary?.trackingStatusKey,
  });
  const resolvedConnectionStatus = pausedLabel ? "offline" : terminalState?.connectionStatus || connectionStatus;
  const normalizedStatus = normalizeLiveTrackingStatus({
    connectionStatus: resolvedConnectionStatus,
    trackingStatusKey: pausedLabel ? "tracking_paused" : terminalState?.trackingStatusKey || summary?.trackingStatusKey || null,
    trackingStatusLabel: pausedLabel || terminalState?.trackingStatusLabel || summary?.trackingStatusLabel || "Tracking unavailable",
    telemetry,
  });

  return {
    id: snapshot.id,
    raw: data,
    telemetry,
    summary,
    position,
    coordinatesLabel: buildCoordinateLabel(position),
    speedKph:
      Number.isFinite(Number(summary?.currentSpeedKph))
        ? Number(summary.currentSpeedKph)
        : Number.isFinite(Number(telemetry?.lastKnownSpeedKph))
        ? Number(telemetry.lastKnownSpeedKph)
        : null,
    speedLimitKph: normalizeRoadSpeedLimitKph(summary?.currentRoadSpeedLimitKph),
    displaySpeedLimitKph:
      normalizeRoadSpeedLimitKph(summary?.currentRoadDisplaySpeedLimitKph) ||
      normalizeRoadSpeedLimitKph(summary?.currentRoadSpeedLimitKph),
    speedLimitEstimated: Boolean(summary?.currentRoadDisplaySpeedLimitEstimated),
    speedLimitSource: summary?.currentRoadDisplaySpeedLimitSource || null,
    speedLimitMessage: summary?.currentRoadDisplaySpeedLimitMessage || null,
    providerStatus: summary?.providerStatus || summary?.speedingAnalysis?.providerStatus || null,
    providerMessage: summary?.providerMessage || summary?.speedingAnalysis?.providerMessage || null,
    lastTelemetryAtMs,
    lastTelemetryAt: lastTelemetryAtMs ? new Date(lastTelemetryAtMs).toISOString() : null,
    heartbeatAgeMs,
    status: data?.status,
    connectionStatus: resolvedConnectionStatus,
    roadName: summary?.currentRoadName || summary?.currentRoadAddress || null,
    trackingStatusKey: normalizedStatus.trackingStatusKey,
    trackingStatusLabel: normalizedStatus.trackingStatusLabel,
    isTrackingTerminal: Boolean(terminalState),
    routeSnapshot: data?.driverRouteSnapshot || null,
    driverName: data?.driver?.user?.name || data?.driver?.name || null,
    vehicleRegistration: data?.vehicleRegNumber || data?.VehicleRegNo || null,
    vehicleType: data?.vehicleType || data?.VehicleType || null,
  };
}

export function mapInfringementSnapshot(snapshot) {
  const data = snapshot.data() || {};
  const peakPosition = normalizeCoordinate(data?.peakLatitude, data?.peakLongitude);
  const startPosition = normalizeCoordinate(data?.startLatitude, data?.startLongitude);
  const endPosition = normalizeCoordinate(data?.endLatitude, data?.endLongitude);
  const position = peakPosition || startPosition || endPosition || normalizeCoordinate(data?.latitude, data?.longitude);
  const eventAtMs =
    toMillis(data?.eventAt) ||
    toMillis(data?.printedAt) ||
    toMillis(data?.endTime) ||
    toMillis(data?.startTime) ||
    toMillis(data?.createdAt);

  return {
    id: snapshot.id,
    ...data,
    speedLimitKph: data?.speedLimitKph ?? data?.roadSpeedLimitKph ?? null,
    actualSpeedKph: data?.actualSpeedKph ?? data?.maxSpeedKph ?? null,
    deltaKph: data?.deltaKph ?? data?.amountOverLimitKph ?? null,
    amountOverTriggerKph: data?.amountOverTriggerKph ?? null,
    triggerToleranceKph: data?.triggerToleranceKph ?? null,
    triggerSpeedKph: data?.triggerSpeedKph ?? null,
    speedInfringementRuleId: data?.speedInfringementRuleId ?? null,
    durationSeconds: data?.durationSeconds ?? data?.durationOverLimitSeconds ?? null,
    position,
    coordinatesLabel: buildCoordinateLabel(position),
    eventAtMs,
    eventAt: eventAtMs ? new Date(eventAtMs).toISOString() : null,
    printedAtMs: toMillis(data?.printedAt),
    offlineStartedAtMs: toMillis(data?.offlineStartedAt),
    startTimeMs: toMillis(data?.startTime),
    endTimeMs: toMillis(data?.endTime),
    checkpointTimeMs: toMillis(data?.checkpointTime),
    reviewStatus: data?.reviewStatus || "pending",
    reviewNote: data?.reviewNote || "",
    reviewedAtMs: toMillis(data?.reviewedAt),
    confidenceValue: Number.isFinite(Number(data?.confidence)) ? Number(data.confidence) : null,
  };
}

export function mapTrackingEventSnapshot(snapshot) {
  const data = snapshot.data() || {};
  const createdAtMs = toMillis(data?.createdAt);
  const clientTimestampMs = toMillis(data?.clientTimestamp);
  const initialRecordedAtMs = toMillis(data?.initialRecordedAt);
  const finalRecordedAtMs = toMillis(data?.finalRecordedAt);
  const gapStartedAtMs = toMillis(data?.gapStartedAt);
  const gapResolvedAtMs = toMillis(data?.gapResolvedAt);
  const sortAtMs =
    gapResolvedAtMs ||
    gapStartedAtMs ||
    finalRecordedAtMs ||
    initialRecordedAtMs ||
    clientTimestampMs ||
    createdAtMs;

  return {
    id: snapshot.id,
    ...data,
    createdAtMs,
    clientTimestampMs,
    initialRecordedAtMs,
    finalRecordedAtMs,
    gapStartedAtMs,
    gapResolvedAtMs,
    sortAtMs,
  };
}

export function mapTrackingSampleSnapshot(snapshot) {
  const data = snapshot.data() || {};
  const recordedAtMs = toMillis(data?.recordedAt);
  const createdAtMs = toMillis(data?.createdAt);
  const position =
    normalizeCoordinate(data?.rawLatitude, data?.rawLongitude) ||
    normalizeCoordinate(data?.latitude, data?.longitude);

  return {
    id: snapshot.id,
    ...data,
    position,
    coordinatesLabel: buildCoordinateLabel(position),
    recordedAtMs,
    createdAtMs,
    sortAtMs: recordedAtMs || createdAtMs,
  };
}

export function mapComplianceEventSnapshot(snapshot) {
  const data = snapshot.data() || {};
  const eventAtMs = toMillis(data?.eventAt);
  const createdAtMs = toMillis(data?.createdAt);
  const resolvedAtMs = toMillis(data?.resolvedAt);
  const provisionalResolvedAtMs = toMillis(data?.provisionalResolvedAt);
  const gapResolvedAtMs = toMillis(data?.gapResolvedAt);
  const closedAtMs = toMillis(data?.closedAt);
  const sortAtMs =
    resolvedAtMs ||
    provisionalResolvedAtMs ||
    closedAtMs ||
    gapResolvedAtMs ||
    eventAtMs ||
    createdAtMs;

  return {
    id: snapshot.id,
    ...data,
    eventAtMs,
    createdAtMs,
    resolvedAtMs,
    provisionalResolvedAtMs,
    gapResolvedAtMs,
    closedAtMs,
    sortAtMs,
  };
}

function subscribeTripSubcollection(
  { tripId, collectionName, orderField, limitCount = 50, mapper },
  onData,
  onError
) {
  if (!tripId) return () => {};

  return onSnapshot(
    query(
      collection(db, "tripOrders", String(tripId), collectionName),
      orderBy(orderField, "desc"),
      limit(limitCount)
    ),
    (snapshot) => onData(snapshot.docs.map(mapper)),
    onError
  );
}

export function subscribeTripLiveTracking(tripId, onData, onError) {
  if (!tripId) return () => {};
  return onSnapshot(
    doc(db, "tripOrders", String(tripId)),
    (snapshot) => onData(mapTripTrackingSnapshot(snapshot)),
    onError
  );
}

export async function loadTripLiveTracking(tripId) {
  if (!tripId) return null;
  const snapshot = await getDoc(doc(db, "tripOrders", String(tripId)));
  return mapTripTrackingSnapshot(snapshot);
}

export function subscribeTripTrackingEvents({ tripId, limitCount = 50 } = {}, onData, onError) {
  return subscribeTripSubcollection(
    {
      tripId,
      collectionName: "trackingEvents",
      orderField: "createdAt",
      limitCount,
      mapper: mapTrackingEventSnapshot,
    },
    onData,
    onError
  );
}

export function subscribeTripTrackingSamples({ tripId, limitCount = 500 } = {}, onData, onError) {
  return subscribeTripSubcollection(
    {
      tripId,
      collectionName: "trackingSamples",
      orderField: "recordedAt",
      limitCount,
      mapper: mapTrackingSampleSnapshot,
    },
    onData,
    onError
  );
}

export function subscribeTripComplianceEvents({ tripId, limitCount = 50 } = {}, onData, onError) {
  return subscribeTripSubcollection(
    {
      tripId,
      collectionName: "complianceEvents",
      orderField: "eventAt",
      limitCount,
      mapper: mapComplianceEventSnapshot,
    },
    onData,
    onError
  );
}

export function subscribeTripOfflineInfringements({ tripId, limitCount = 50 } = {}, onData, onError) {
  return subscribeTripSubcollection(
    {
      tripId,
      collectionName: "offlineInfringements",
      orderField: "checkpointTime",
      limitCount,
      mapper: mapInfringementSnapshot,
    },
    onData,
    onError
  );
}

export function subscribeTripSpeedingInfringements({ tripId, limitCount = 100 } = {}, onData, onError) {
  return subscribeTripSubcollection(
    {
      tripId,
      collectionName: "infringements",
      orderField: "endTime",
      limitCount,
      mapper: mapInfringementSnapshot,
    },
    onData,
    onError
  );
}

export function subscribeInfringementIndex({ tripId = null, originId = null, originIds = [], limitCount = 250 } = {}, onData, onError) {
  const base = collection(db, "infringementIndex");
  const constraints = [];
  const scopedOriginIds = [...new Set((Array.isArray(originIds) ? originIds : []).filter(Boolean).map(String))];

  if (tripId) {
    constraints.push(where("tripOrderId", "==", String(tripId)));
  }

  if (originId) {
    constraints.push(where("originId", "==", String(originId)));
  } else if (scopedOriginIds.length === 1) {
    constraints.push(where("originId", "==", scopedOriginIds[0]));
  } else if (scopedOriginIds.length > 1) {
    constraints.push(where("originId", "in", scopedOriginIds.slice(0, 30)));
  }

  constraints.push(orderBy("eventAt", "desc"));
  constraints.push(limit(limitCount));

  return onSnapshot(
    query(base, ...constraints),
    (snapshot) => onData(snapshot.docs.map(mapInfringementSnapshot)),
    onError
  );
}

export async function updateInfringementReview({ infringementId, reviewStatus, reviewNote, reviewer }) {
  const payload = {
    updatedAt: serverTimestamp(),
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewer || null,
  };

  if (reviewStatus !== undefined) {
    payload.reviewStatus = reviewStatus;
  }

  if (reviewNote !== undefined) {
    payload.reviewNote = reviewNote;
  }

  await updateDoc(doc(db, "infringementIndex", String(infringementId)), payload);
}

export function subscribeSpeedInfringementRules(onData, onError) {
  return onSnapshot(
    doc(db, SPEED_INFRINGEMENT_SETTINGS_COLLECTION, SPEED_INFRINGEMENT_SETTINGS_DOC),
    (snapshot) => onData(normalizeSpeedInfringementRules(snapshot.exists() ? snapshot.data()?.rules : null)),
    onError
  );
}

export async function saveSpeedInfringementRules(rules, user) {
  const normalizedRules = normalizeSpeedInfringementRules(rules);
  await httpClient.post(Endpoints.SPEED_INFRINGEMENT_RULES, {
    rules: normalizedRules,
    updatedBy: user?.email || user?.name || user?.id || null,
  });
}
